import { Link, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CarFrontFill, PeopleFill, BoxArrowLeft, ShieldLockFill, Tag, PersonBadgeFill, CashStack, PersonLinesFill, TrophyFill } from 'react-bootstrap-icons';
import language from '../../data/language';
import { currentLanguage } from '../../common/ui/notify';
import { LoadingBlock } from '../../common/ui/StateBlock';
import './Admin.css';
import '../../common/ui/ui.css';

// Roles allowed into the panel. Unchanged from the API: an ordinary visitor has
// no role at all and therefore never gets in.
const STAFF_ROLES = ['admin', 'moderator'];

export function isStaff(user) {
    return Boolean(user && user.isLoggedIn && STAFF_ROLES.includes(user.role));
}

/**
 * Shell for every admin screen: guard, sidebar, page header, content area.
 * Individual pages only render their own content.
 */
const AdminLayout = ({title, subtitle, actions, children}) => {
    const lang = currentLanguage();
    const strings = language[lang].admin;
    const common = language[lang].common;
    const user = useSelector((state) => state.user);

    // Auction section labels are inline (not in the shared language file) to keep
    // this increment self-contained.
    const A = ({ geo, en, ru, am }) => ({ geo, en, ru, am }[lang] || geo);
    const navItems = [
        {to: "/admin/cars", label: strings.cars, icon: <CarFrontFill />},
        {to: "/admin/leads", label: strings.leads, icon: <PeopleFill />},
        {to: "/admin/auction/events", label: A({geo: "აუქციონები", en: "Auctions", ru: "Аукционы", am: "Աճուրդ"}), icon: <Tag />},
        {to: "/admin/auction/bidders", label: A({geo: "ბიდერები", en: "Bidders", ru: "Бидеры", am: "Բիդերներ"}), icon: <PersonLinesFill />},
        {to: "/admin/auction/won", label: A({geo: "მოგებული", en: "Won cars", ru: "Выигранные", am: "Շահածները"}), icon: <TrophyFill />},
        {to: "/admin/auction/kyc", label: A({geo: "KYC", en: "KYC", ru: "KYC", am: "KYC"}), icon: <PersonBadgeFill />},
        {to: "/admin/auction/deposits", label: A({geo: "დეპოზიტები", en: "Deposits", ru: "Депозиты", am: "Ավանդներ"}), icon: <CashStack />}
    ];

    // Until /api/user has answered we cannot tell a logged-out visitor from an
    // admin who just refreshed the page.
    if (!user.authChecked) {
        return (
            <div className="admin-page">
                <div className="container">
                    <LoadingBlock label={common.loading} />
                </div>
            </div>
        );
    }

    if (!isStaff(user)) {
        return (
            <div className="admin-page">
                <div className="container">
                    <div className="admin-denied">
                        <div className="admin-denied-icon" aria-hidden="true"><ShieldLockFill /></div>
                        <h1>{strings.access_denied_title}</h1>
                        <p>{strings.access_denied_message}</p>
                        <Link to="/login" className="cx-btn cx-btn-primary">{strings.login}</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="container">
                <div className="admin-shell">
                    <aside className="admin-sidebar">
                        <div className="admin-sidebar-heading">
                            <span className="admin-sidebar-label">{strings.panel}</span>
                            <span className="admin-sidebar-user">{user.name || user.username}</span>
                        </div>

                        <nav className="admin-nav" aria-label={strings.panel}>
                            {navItems.map((item) => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    className="admin-nav-item"
                                    activeClassName="is-active"
                                >
                                    <span className="admin-nav-icon" aria-hidden="true">{item.icon}</span>
                                    <span>{item.label}</span>
                                </NavLink>
                            ))}
                        </nav>

                        <Link to="/" className="admin-nav-item admin-nav-back">
                            <span className="admin-nav-icon" aria-hidden="true"><BoxArrowLeft /></span>
                            <span>{strings.back_to_site}</span>
                        </Link>
                    </aside>

                    <section className="admin-content">
                        <header className="admin-content-header">
                            <div>
                                <h1 className="admin-content-title">{title}</h1>
                                {subtitle && <p className="admin-content-subtitle">{subtitle}</p>}
                            </div>
                            {actions && <div className="admin-content-actions">{actions}</div>}
                        </header>

                        {children}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default AdminLayout;
